import { array, fn } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, RequestError, Service } from 'webapp-types';

import highlightResult from 'webapp/helpers/highlight-result';
import joinClasses from 'webapp/helpers/join-classes';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import BoardImagePreviewComponent from 'webapp/components/board/image-preview';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DummyCellComponent from 'webapp/components/dummy-cell';
import FlexibleToggleCellComponent from 'webapp/components/flexible-toggle-cell';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import OptionsButtonComponent from 'webapp/components/options-button';
import OwnerSelectComponent from 'webapp/components/owner-select';
import TagContainerComponent from 'webapp/components/tag-container';

import type BatchSelect from 'webapp/models-custom/batch-select';
import type ReportSchedule from 'webapp/models-custom/report-schedule';

import type { TrackingOriginTypeForSchedulePopup } from 'webapp/services/model-manager/reports';

interface Args {
	model: ReportSchedule;
	batchSelect: BatchSelect<ReportSchedule>;
	trackingOrigin?: TrackingOriginTypeForSchedulePopup;
}

type Action =
	| 'view'
	| 'edit'
	| 'downloadPdf'
	| 'reschedule'
	| 'sendNow'
	| 'delete' /* | 'approve' | 'unapprove' | 'skip' | 'unskip'*/;
type Option = PopupListItem<Action>;

export default class ReportsSchedulesTableRowComponent extends Component<Args> {
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/report-schedules')
	declare mmReportSchedulesService: Service.ModelManager.ReportSchedules;
	@service('model-manager/report-schedule-settings')
	declare mmReportScheduleSettingsService: Service.ModelManager.ReportScheduleSettings;

	@tracked isVisibleOnScreen = false;
	@tracked showAll = false;

	get trackingOrigin(): TrackingOriginTypeForSchedulePopup {
		return this.args.trackingOrigin || 'reports_scheduled';
	}

	get scheduleIcon(): string {
		return 'paperplane';
		// const model = this.args.model;
		// if (model.isSkipped) {
		// 	return 'skip';
		// } else if (model.isMissingApproval) {
		// 	return 'stopwatch';
		// } else {
		// 	return 'paperplane';
		// }
	}

	// @cached
	// get isMultipleApprovals(): boolean {
	// 	const setting = this.args.model.reportScheduleSetting;
	// 	return !!setting
	// 		&& setting.approvalIdsUsers.length > 1;
	// }

	get options(): Option[] {
		const options: Option[] = [];

		const canEditSetting = this.args.model.reportScheduleSetting?.canCurrentUserEdit,
			isOneTimeSchedule = this.args.model.isOneOff,
			oneTimeScheduleHasReportUrl = isOneTimeSchedule && this.args.model.reportUrl,
			automatedScheduleHasReport = !isOneTimeSchedule && this.args.model.report;

		if (oneTimeScheduleHasReportUrl) {
			options.push({
				icon: 'Pdf',
				text: 'View PDF',
				action: 'downloadPdf',
			});
		}

		if (automatedScheduleHasReport) {
			options.push({
				icon: 'Databoards',
				text: 'View',
				action: 'view',
			});
		}

		if (canEditSetting) {
			if (isOneTimeSchedule) {
				if (this.args.model.timeLeft > 0) {
					options.push({
						icon: 'Clock',
						text: 'Reschedule',
						action: 'reschedule',
					});

					options.push({
						icon: 'Send',
						text: 'Send now',
						action: 'sendNow',
					});
				}
			} else {
				options.push({
					icon: 'Pencil',
					text: 'Edit schedule',
					action: 'edit',
				});
			}

			options.push({
				class: 'delete',
				text: 'Delete',
				action: 'delete',
				icon: 'Trash',
			});
		}

		// if (this.args.model.reportScheduleSetting?.needsMyApprovalToSend) {
		// 	if (this.args.model.isMissingMyApproval) {
		// 		options.push({
		// 			class: 'check',
		// 			text: 'Approve',
		// 			action: 'approve'
		// 		});
		// 	} else {
		// 		options.push({
		// 			class: 'check',
		// 			text: 'Unapprove',
		// 			action: 'unapprove'
		// 		});
		// 	}
		// }

		// if (this.args.model.canSkip) {
		// 	if (this.args.model.isSkipped) {
		// 		options.push({
		// 			class: 'skip',
		// 			text: 'Unskip',
		// 			action: 'unskip'
		// 		});
		// 	} else {
		// 		options.push({
		// 			class: 'skip',
		// 			text: 'Skip',
		// 			action: 'skip'
		// 		});
		// 	}
		// }

		return options;
	}

	get isSelected(): boolean {
		const { batchSelect, model } = this.args;
		return batchSelect.isItemSelected(model);
	}

	@action
	doOptionsAction(actionName: string): void {
		this[actionName as Action]();
	}

	@action
	downloadPdf(): void {
		window.open(this.args.model.reportUrl, '_blank');
	}

	@action
	edit(): void {
		const reportScheduleSetting = this.args.model.reportScheduleSetting;

		if (!reportScheduleSetting) {
			return;
		}

		this.mmReportScheduleSettingsService.edit(reportScheduleSetting, this.trackingOrigin);
	}

	@action
	view(): void {
		this.mmReportsService.openPreviewPopup(this.args.model.report!, 'reports_scheduled');
	}

	@action
	reschedule(): void {
		const reportScheduleSetting = this.args.model.reportScheduleSetting;

		if (!reportScheduleSetting) {
			return;
		}

		this.mmReportSchedulesService.openSendLaterPopup({
			initialTimestamp: reportScheduleSetting.scheduledOn,
			onSave: (ts: number): Promise<void> => {
				reportScheduleSetting.scheduledOn = ts;

				const layer = this.infolayerService.loading('Rescheduling...');

				return new Promise((resolve, reject) => {
					reportScheduleSetting
						.save()
						.then(
							() => {
								layer.ok('Rescheduled!');
								resolve();
							},
							(errorData) => {
								layer.close();
								throw errorData;
							},
						)
						.catch((errorData: RequestError) => {
							this.requestService.showAllValidationErrors(errorData);
							reject();
						});
				});
			},
		});
	}

	@action
	sendNow(): void {
		const reportScheduleSetting = this.args.model.reportScheduleSetting;

		if (!reportScheduleSetting) {
			return;
		}

		if (!reportScheduleSetting.isOneOff) {
			return;
		}

		reportScheduleSetting.scheduledOn = Math.round(Date.now() / 1000);

		const layer = this.infolayerService.loading('Rescheduling...');

		reportScheduleSetting
			.save()
			.then(
				() => {
					layer.ok('Rescheduled!');
				},
				(errorData) => {
					layer.close();
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	delete(): void {
		const reportScheduleSetting = this.args.model.reportScheduleSetting;

		if (!reportScheduleSetting) {
			return;
		}
		this.args.batchSelect.unselectAll();
		this.mmReportScheduleSettingsService.delete(reportScheduleSetting);
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}

	@action
	toggleShowAll() {
		this.showAll = !this.showAll;
	}

	// @action
	// approve(): void {
	// 	const layer = this.infolayerService.loading('Approval in progress...');
	// 	this.mmReportSchedulesService.approveSchedule(this.args.model).then(() => {
	// 		layer.ok('Approved!');
	// 	}, (errorData) => {
	// 		layer.close();
	// 		throw errorData;
	// 	}).catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	// }

	// @action
	// unapprove(): void {
	// 	const layer = this.infolayerService.loading('Unapproving...');
	// 	this.mmReportSchedulesService.unapproveSchedule(this.args.model).then(() => {
	// 		layer.ok('Unapproved!');
	// 	}, (errorData) => {
	// 		layer.close();
	// 		throw errorData;
	// 	}).catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	// }

	// @action
	// skip(): void {
	// 	const layer = this.infolayerService.loading('Skip in progress...');
	// 	this.mmReportSchedulesService.skipSchedule(this.args.model).then(() => {
	// 		layer.ok('Skipped!');
	// 	}, (errorData) => {
	// 		layer.close();
	// 		throw errorData;
	// 	}).catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	// }

	// @action
	// unskip(): void {
	// 	const layer = this.infolayerService.loading('Unskipping...');
	// 	this.mmReportSchedulesService.unskipSchedule(this.args.model).then(() => {
	// 		layer.ok('Unskipped!');
	// 	}, (errorData) => {
	// 		layer.close();
	// 		throw errorData;
	// 	}).catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	// }
	<template>
		{{#if this.isVisibleOnScreen}}
			<tr
				class={{joinClasses
					(array
						"table-row" (if this.showAll "all-shown") (if this.isSelected "selected")
					)
				}}
				data-id={{@model.id}}
			>
				<td class="cc">
					<FormCheckboxComponent
						@name="report-schedules-table-row"
						@selected={{this.isSelected}}
						@theme="blue"
						@onChange={{fn @batchSelect.changeSelection @model}}
					/>
				</td>
				<td class="c0 board no-grow first">
					<BoardImagePreviewComponent
						@image={{@model.report.image}}
						@fallbackImage={{@model.report.fallbackImage}}
						@color={{@model.report.color}}
						@isLazyLoaded={{true}}
						@onImageClick={{this.view}}
					/>
				</td>
				<td class="c1">
					<strong>
						{{highlightResult text=@model.report.title match=@model.activeFilter}}
					</strong>
					<small>
						{{@model.report.slideCountText}}
					</small>
				</td>

				<td class="c2 schedule-frequency prio-low" data-label="Frequency: ">
					<span class="schedule-icon-wrapper">
						<CommonUiIconComponent @name="Clock" @size="sm" />
						<strong>{{@model.reportScheduleSetting.schedule.typeText}}</strong>
						{{@model.reportScheduleSetting.schedule.timeText}}
					</span>
				</td>

				<td class="c3 prio-high" data-label="Sending time: ">
					<span class="schedule-icon-wrapper">
						<CommonUiIconComponent @name="Send" @size="sm" />
						{{{@model.sendStatusText}}}
					</span>
				</td>

				<td class="c4 prio-low" data-label="Owner: ">
					{{#if @model.report}}
						<OwnerSelectComponent @model={{@model.report}} @canEdit={{false}} />
					{{/if}}
				</td>
				<td class="prio-low" data-label="Tags: ">
					{{#if @model.report}}
						<TagContainerComponent @model={{@model.report}} @canEdit={{false}} />
					{{/if}}
				</td>

				<td class="c6 button-ct last">
					<OptionsButtonComponent
						@options={{this.options}}
						@onAction={{this.doOptionsAction}}
					/>
				</td>

				<FlexibleToggleCellComponent
					@showAll={{this.showAll}}
					@toggleShowAll={{this.toggleShowAll}}
				/>
			</tr>

		{{else}}

			<tr class="table-row" {{visibleCheckItem this.visibleChanged}}>
				<DummyCellComponent />
			</tr>

		{{/if}}
	</template>
}
